/**
 * Failed-run spend alert settings (SPRITE-1307).
 *
 * Staff alert: when one org's failed-run provider cost (0 credits charged) reaches the
 * threshold in the rolling window, the credits-worker sweep posts to this Discord/Slack
 * webhook, at most once per org per window. Stored in D1 system_settings.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getFailedSpendAlertSetting,
  updateFailedSpendAlertSetting,
} from '../utils/adminApi';

const INPUT_CLASS =
  'w-full rounded-md bg-gray-900 border border-gray-700 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

interface FormState {
  webhookUrl: string;
  thresholdUsd: string;
  windowHours: string;
}

const toForm = (c: {
  webhookUrl: string;
  thresholdUsd: number;
  windowHours: number;
}): FormState => ({
  webhookUrl: c.webhookUrl,
  thresholdUsd: String(c.thresholdUsd),
  windowHours: String(c.windowHours),
});

const FailedSpendAlertSettings: React.FC = () => {
  const { idToken } = useAuth();

  const [form, setForm] = useState<FormState>({
    webhookUrl: '',
    thresholdUsd: '',
    windowHours: '',
  });
  const [saved, setSaved] = useState<FormState | null>(null);
  const [invalidStored, setInvalidStored] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getFailedSpendAlertSetting(idToken);
        if (cancelled) return;
        const next = toForm(res);
        setForm(next);
        setSaved(next);
        setInvalidStored(res.invalid);
        setUpdatedAt(res.updatedAt);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load settings'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const dirty =
    !saved ||
    invalidStored ||
    form.webhookUrl.trim() !== saved.webhookUrl ||
    form.thresholdUsd !== saved.thresholdUsd ||
    form.windowHours !== saved.windowHours;

  const update =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      setSavedNotice(false);
    };

  const handleSave = async () => {
    if (!idToken || saving || !dirty) return;
    setSaving(true);
    setError(null);
    setSavedNotice(false);
    try {
      const res = await updateFailedSpendAlertSetting(idToken, {
        webhookUrl: form.webhookUrl.trim(),
        thresholdUsd: Number(form.thresholdUsd),
        windowHours: Number(form.windowHours),
      });
      const next = toForm(res);
      setForm(next);
      setSaved(next);
      setInvalidStored(false);
      setUpdatedAt(new Date().toISOString());
      setSavedNotice(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-white">
        Failed-run spend alert
      </h2>
      <p className="text-gray-400 text-sm mt-1">
        Runs that fail on the provider&apos;s side cost the customer no credits,
        but we still pay for them. When one organization&apos;s provider cost on
        failed runs reaches the threshold within the window, a message is posted
        to this Discord or Slack webhook, at most once per organization per
        window. Checked every 10 minutes. Leave the webhook empty to disable.
      </p>

      {loading ? (
        <div className="mt-4 text-gray-400">Loading…</div>
      ) : (
        <div className="mt-4 space-y-3">
          {invalidStored && (
            <div role="alert" className="text-sm text-yellow-400">
              The saved settings are no longer valid, so the alert is off.
              Review the values below and save to turn it back on.
            </div>
          )}
          <label
            htmlFor="failed-spend-webhook-url"
            className="block text-sm font-medium text-gray-300"
          >
            Webhook URL
          </label>
          <input
            id="failed-spend-webhook-url"
            type="url"
            value={form.webhookUrl}
            onChange={update('webhookUrl')}
            placeholder="https://discord.com/api/webhooks/… or https://hooks.slack.com/services/…"
            autoComplete="off"
            spellCheck={false}
            className={INPUT_CLASS}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="failed-spend-threshold"
                className="block text-sm font-medium text-gray-300"
              >
                Threshold (USD)
              </label>
              <input
                id="failed-spend-threshold"
                type="number"
                min={1}
                step="any"
                value={form.thresholdUsd}
                onChange={update('thresholdUsd')}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label
                htmlFor="failed-spend-window"
                className="block text-sm font-medium text-gray-300"
              >
                Window (hours)
              </label>
              <input
                id="failed-spend-window"
                type="number"
                min={1}
                max={720}
                step={1}
                value={form.windowHours}
                onChange={update('windowHours')}
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="text-sm text-red-400">
              {error}
            </div>
          )}
          {savedNotice && !error && (
            <div role="status" className="text-sm text-green-400">
              Saved. {form.webhookUrl.trim() === '' && 'Alert disabled.'}
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {updatedAt && (
              <span className="text-xs text-gray-500">
                Last updated {new Date(updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FailedSpendAlertSettings;
